import BlockContent from "@sanity/block-content-to-react"
import CalloutBanner from "@components/common/CalloutBanner"
import CalloutQuote from "@components/common/CalloutQuote"
import ContactUs from "@components/common/ContactUs"
import { GatsbyImage } from "gatsby-plugin-image"
import React from "react"
import ReactPlayer from "react-player/lazy"
import { SanityRawText } from "./globalTypes"
import TestConfirmBanner from "@components/common/Banner"
import { forms } from "@utils/globalTypes"
import { getGatsbyImageData } from "gatsby-source-sanity"
import * as s from "./richText.module.scss"
import { sanity } from "../../client-config"
import { Link } from "gatsby"

const Figure = ({ node }) => {
  const { dataset, projectId } = sanity
  const fluidProps = getGatsbyImageData(
    node.asset._id,
    { placeholder: "blurred", fit: "fill" },
    { dataset, projectId }
  )

  return (
    <figure>
      <GatsbyImage image={fluidProps} alt={node.alt} />
    </figure>
  )
}

const renderForm = (formName: string) => {
  switch (formName) {
    case forms.contact_us:
      return <ContactUs />
    default:
      return null
  }
}

export const defaultSerializers = {
  marks: {
    italic: (props) => <em>{props.children}</em>,
    link: ({ mark, children }) => {
      const { href } = mark
      return href && href?.charAt(0) === "/" ? (
        <Link to={href}>{children}</Link>
      ) : (
        <a href={href} target="_blank">
          {children}
        </a>
      )
    },
    color: (props) => (
      <span style={{ color: props.mark.hex }}>{props.children}</span>
    ),
    highlight: (props) => <mark>{props.children}</mark>,
    underline: (props) => (
      <div style={{ textDecoration: "underline" }}>{props.children}</div>
    ),
    AlignCenter: (props) => (
      <div style={{ textAlign: "center" }}>{props.children}</div>
    ),
    AlignJustify: (props) => (
      <div style={{ textAlign: "justify" }}>{props.children}</div>
    ),
    AlignLeft: (props) => (
      <div style={{ textAlign: "left" }}>{props.children}</div>
    ),
    AlignRight: (props) => (
      <div style={{ textAlign: "right" }}>{props.children}</div>
    ),
  },
  types: {
    embebedCode: (props) => (
      <div dangerouslySetInnerHTML={{ __html: props.node.code }} />
    ),
    code: (props) => (
      <pre data-language={props.node.language}>
        <code>{props.node.code}</code>
      </pre>
    ),
    italic: ({ node }) => <em>{node.text}</em>,
    reference: () => <div></div>,
    testConfirmBanner: (props) => (
      <div className={s.bannerContainer}>
        <TestConfirmBanner banner={props?.node} />
      </div>
    ),
    calloutBanner: (props) => (
      <div className={s.bannerContainer}>
        <CalloutBanner banner={props?.node} />
      </div>
    ),
    calloutQuote: (props) => (
      <CalloutQuote quote={props.node.quote} source={props.node.source} />
    ),
    mainImage: Figure,
    videoEmbed: (props) => <ReactPlayer url={props.node.url} />,
    formEmbed: ({ node }) => (
      <div className={s.formContainer}>{renderForm(node.formName)}</div>
    ),
  },
}

type Props = {
  blocks: SanityRawText[]
  serializers?
  className?: string
}

export const BaseBlockContent: React.FC<Props> = ({
  blocks,
  serializers,
  className,
}) => {
  const serializersDict = serializers || defaultSerializers
  return (
    <BlockContent
      serializers={serializersDict}
      blocks={blocks}
      className={className}
    />
  )
}
